import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
import Layout from "../../../Layout";
import { useCallbackPrompt } from "~/hooks/useCallbackPrommpt";
import VerticalStepper from "~/components/stepper";
import ConfirmationDialog from "~/components/confirmation-dialog/ConfirmationDialog";
import CustomeButton from "~/components/Button";
import { useTenantOnboarding } from "./hooks/useTenantOnboarding";
import { Step1TenantInfo } from "./components/Step1TenantInfo";
import { Step2CustomerAdmin } from "./components/Step2CustomerAdmin";
import { TenantOnboardingSuccess } from "./components/TenantOnboardingSuccess";
import ExitConfirmationModal from "~/Pages/Tenants/modals/ExitConfirmationModal";
import { FEATURE_FLAGS, useFeatureFlag } from "~/config/ff";
import { useUserContext } from "./hooks/useUserContext";
import {
  tenantOnboardingActions,
  tenantOnboardingStore,
} from "./store/tenantOnboardingStore";
import { useStoreSelector } from "~/lib/store/hooks";
import { useOnlineStatus } from "~/hooks/useOnlineStatus";
import "./tenant-onboarding.scss";

// TODO: Replace with proper organization ID retrieval from user context
// Temporary hardcoded organization ID for development
const TEMP_ORGANIZATION_ID = "931ddb11-d2d0-4ccf-93d9-8741aee4d799";

const TenantsOnboard: React.FC = () => {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const { value: isTenantFeatureEnabled, isLoading: isFeatureFlagLoading } =
    useFeatureFlag(FEATURE_FLAGS.TENANT, false);
  // TODO: Use actual organizationId from useUserContext() once the Redux state structure is confirmed
  // const { organizationId } = useUserContext();
  const organizationId = TEMP_ORGANIZATION_ID;

  const {
    activeStep,
    isDirty,
    handleNext,
    handleBack,
    handleSubmit,
    isStep1Valid,
    isStep2Valid,
    loading,
    reset,
    toastRef,
    isSuccess,
    successTenantId,
    successTenantName,
    clearSuccess,
  } = useTenantOnboarding();

  const validationErrors = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.validationErrors,
  );

  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(isDirty);

  // State for exit confirmation modal (Cancel button)
  const [showExitModal, setShowExitModal] = useState(false);

  useEffect(() => {
    // Set organization ID from user context first, then reset
    // This ensures organizationId is preserved during reset
    if (organizationId) {
      tenantOnboardingActions.setOrganizationId(organizationId);
    }
  }, [organizationId]);

  useEffect(() => {
    // Reset store on mount, but only after organizationId is set
    // The reset function preserves organizationId, so we can call it safely
    reset();
  }, [reset]);

  const handleLeaveConfirm = () => {
    reset();
    confirmNavigation();
  };

  const handleCancelClick = () => {
    setShowExitModal(true);
  };

  const handleExitConfirm = () => {
    setShowExitModal(false);
    reset();
    navigate(-1);
  };

  const handleExitCancel = () => {
    setShowExitModal(false);
  };

  const steps = [
    {
      label: "General Information",
      content: <Step1TenantInfo />,
    },
    {
      label: "Admin Information",
      content: <Step2CustomerAdmin />,
    },
  ];

  // Show loading while feature flag is loading
  if (isFeatureFlagLoading) {
    return (
      <Layout
        title="Create Tenant"
        crumbs={[
          { label: "Tenants", url: "/tenants", id: "tenants" },
          { label: "Create Tenant", id: "create-tenant" },
        ]}
      >
        <div className="card">
          <div className="flex flex-column align-items-center justify-content-center p-6">
            <p>Loading...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Show forbidden if feature flag is disabled
  if (!isTenantFeatureEnabled && false) {
    // todo create the feature in devcycle
    return (
      <Layout
        title="Create Tenant"
        crumbs={[
          { label: "Tenants", url: "/tenants", id: "tenants" },
          { label: "Create Tenant", id: "create-tenant" },
        ]}
      >
        <div className="card">
          <div className="flex flex-column align-items-center justify-content-center p-6">
            <p>This feature is not available.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title="Create Tenant"
      crumbs={[
        { label: "Tenants", url: "/tenants", id: "tenants" },
        { label: "Create Tenant", id: "create-tenant" },
      ]}
    >
      <div className="grid tenant-onboarding">
        <div className="lg:col-3 md:col-12 sm:col-12 xs:col-12 pt-4">
          <Card className="h-full">
            <p className="heading">Add New Tenant</p>
            <p className="description m-0">
              Complete the steps below to onboard a new tenant
            </p>
            <Divider />
            <div className="step-menu">
              <VerticalStepper
                options={steps}
                activeIndex={isSuccess ? steps.length : activeStep}
              />
            </div>
          </Card>
        </div>
        <div className="lg:col-9 md:col-12 sm:col-12 xs:col-12 pt-4">
          <Card className="card-onboard">
            {isSuccess && successTenantId && successTenantName ? (
              <TenantOnboardingSuccess
                tenantName={successTenantName}
                tenantId={successTenantId}
                onClose={clearSuccess}
              />
            ) : (
              steps[activeStep]?.content
            )}
          </Card>
          {!isSuccess && (
            <div
              className={`next-btn-footer ${activeStep === 0 ? "flex-row-reverse" : ""}`}
            >
              {activeStep !== 0 ? (
                <div className="d-flex justify-content-start">
                  <button
                    type="button"
                    className="generic-cancel"
                    onClick={handleCancelClick}
                  >
                    Cancel
                  </button>
                </div>
              ) : null}
              <div className="flex justify-content-end gap-2">
                {activeStep === 0 ? (
                  <button
                    type="button"
                    className="cancel-btns"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    className="cancel-btns"
                  >
                    Back
                  </button>
                )}
                <CustomeButton
                  label={
                    activeStep === steps.length - 1 ? "Create Tenant" : "Next"
                  }
                  type="submit"
                  iconPos="right"
                  onClick={() => {
                    activeStep === steps.length - 1
                      ? handleSubmit()
                      : handleNext();
                  }}
                  loading={loading}
                  disabled={
                    !isOnline ||
                    (activeStep === 0 && !isStep1Valid) ||
                    (activeStep === 1 && !isStep2Valid) ||
                    loading
                  }
                  className={`add-new-lsp ${
                    (!isOnline ||
                      (activeStep === 0 && !isStep1Valid) ||
                      (activeStep === 1 && !isStep2Valid)) &&
                    "disable"
                  }`}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {showPrompt && (
        <ConfirmationDialog
          show={showPrompt}
          onConfirm={handleLeaveConfirm}
          onCancel={cancelNavigation}
        />
      )}
      <ExitConfirmationModal
        visible={showExitModal}
        onCancel={handleExitCancel}
        onConfirm={handleExitConfirm}
      />
      <Toast ref={toastRef} position="bottom-right" />
    </Layout>
  );
};

export default TenantsOnboard;
